/**
 *	Client-side key pair generation.
 *
 *	The private half is made here and never leaves the tab: only `publicKey`
 *	goes into a create or rotate request, which is what lets a dump of the key
 *	table stay harmless. It is also what makes a lost private key unrecoverable
 *	— rotation is the only answer.
 *
 *	WebCrypto needs a secure context, which localhost counts as, so the dev
 *	server works unchanged.
 */
const KeyAlgorithm: EcKeyGenParams = { name: 'ECDSA', namedCurve: 'P-256' };

/**
 *	Extractable, because both halves have to come back out as PEM; for a pair
 *	the flag governs the private key, the public one always being extractable.
 *	ECDSA requires both usages at generation — `sign` binds to the private key
 *	and `verify` to the public one.
 */
const KeyUsages: KeyUsage[] = ['sign', 'verify'];

export interface GeneratedKeypair {
	/** SPKI, PEM-armoured. The only half that is ever transmitted. */
	publicKey: string;
	/** PKCS#8, PEM-armoured. Shown once, downloaded, then dropped. */
	privateKey: string;
}

export async function generateKeypair(): Promise<GeneratedKeypair> {
	const pair = await crypto.subtle.generateKey(KeyAlgorithm, true, KeyUsages);

	const [spki, pkcs8] = await Promise.all([
		crypto.subtle.exportKey('spki', pair.publicKey),
		crypto.subtle.exportKey('pkcs8', pair.privateKey),
	]);

	return {
		publicKey: toPem('PUBLIC KEY', spki),
		privateKey: toPem('PRIVATE KEY', pkcs8),
	};
}

/**
 *	DER to base64 to PEM, wrapped at 64 columns as RFC 7468 asks.
 *
 *	The spread into `fromCharCode` is safe at these sizes — a P-256 SPKI is 91
 *	bytes and its PKCS#8 around 138 — and would not be for anything larger.
 */
function toPem(label: string, der: ArrayBuffer): string {
	const base64 = btoa(String.fromCharCode(...new Uint8Array(der)));
	const lines = base64.match(/.{1,64}/g) ?? [];

	return `-----BEGIN ${label}-----\n${lines.join('\n')}\n-----END ${label}-----\n`;
}

/**
 *	Hands the private key over as a file.
 *
 *	The object URL is released on the next tick: Safari cancels the download if
 *	it is revoked in the same turn as the click.
 */
export function downloadPrivateKey(fileName: string, privateKey: string): void {
	const url = URL.createObjectURL(
		new Blob([privateKey], { type: 'application/x-pem-file' })
	);

	const anchor = document.createElement('a');

	anchor.href = url;
	anchor.download = `${slugify(fileName)}.pem`;
	anchor.click();

	setTimeout(() => URL.revokeObjectURL(url), 0);
}

function slugify(name: string): string {
	const slug = name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

	return slug || 'api-key';
}
